import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, Loader2, Gamepad2, Target, Users, Clock } from 'lucide-react';
import { apiFetch } from '../utils/api';
import {
    getOrderedVisibleSections,
    buildExpandedMapFromSections,
    DEFAULT_ANALYTICS_SECTIONS
} from '../utils/analyticsSectionsConfig';
import StatisticsAnalyticsSections from './StatisticsAnalyticsSections';
import SessionDetailOverlay from './SessionDetailOverlay';

/**
 * Statistics - Comprehensive game statistics dashboard
 */
const Statistics = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [analyticsSettings, setAnalyticsSettings] = useState(null);
    const [expandedSections, setExpandedSections] = useState(() =>
        buildExpandedMapFromSections(DEFAULT_ANALYTICS_SECTIONS)
    );
    
    // Expanded rows for session details
    const [expandedRows, setExpandedRows] = useState({});
    const [rowSessions, setRowSessions] = useState({});
    const [loadingRow, setLoadingRow] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);

    useEffect(() => {
        fetchStatistics();
        fetchAnalyticsSettings();
    }, []);
    
    const fetchAnalyticsSettings = async () => {
        try {
            const res = await apiFetch('/api/club/analytics-settings');
            if (res.ok) {
                const data = await res.json();
                setAnalyticsSettings(data);
                if (data.sections && data.sections.length > 0) {
                    setExpandedSections(buildExpandedMapFromSections(data.sections));
                }
            }
        } catch (err) {
            console.error('Analytics settings fetch error:', err);
        }
    };

    const fetchStatistics = async () => {
        try {
            const res = await apiFetch('/api/statistics');
            if (!res.ok) throw new Error('Failed to fetch statistics');
            const data = await res.json();
            setStats(data);
        } catch (err) {
            console.error('Statistics fetch error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Toggle row expansion and fetch sessions if needed
    const toggleRowExpansion = async (rowKey, type, id, playerId = null) => {
        const isCurrentlyExpanded = expandedRows[rowKey];
        
        setExpandedRows(prev => ({
            ...prev,
            [rowKey]: !isCurrentlyExpanded
        }));
        
        // If expanding and no sessions loaded yet, fetch them
        if (!isCurrentlyExpanded && !rowSessions[rowKey]) {
            setLoadingRow(rowKey);
            try {
                const params = new URLSearchParams({ type });
                if (id) params.append('id', id);
                if (playerId) params.append('playerId', playerId);
                
                const res = await apiFetch(`/api/statistics/sessions?${params}`);
                if (res.ok) {
                    const sessions = await res.json();
                    setRowSessions(prev => ({
                        ...prev,
                        [rowKey]: sessions
                    }));
                }
            } catch (err) {
                console.error('Failed to fetch sessions:', err);
            } finally {
                setLoadingRow(null);
            }
        }
    };

    const handleSessionDetailClick = async (sessionId) => {
        if (sessionId == null) return;
        try {
            const res = await apiFetch(`/api/sessions/${sessionId}`);
            if (res.ok) {
                const data = await res.json();
                setSelectedSession(data);
            }
        } catch (err) {
            console.error('Session detail fetch error:', err);
        }
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '0 min';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours === 0) return `${mins} min`;
        return `${hours}h ${mins}min`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fi-FI');
    };

    const orderedSections = useMemo(
        () => getOrderedVisibleSections(analyticsSettings),
        [analyticsSettings]
    );

    /** Yhteenveto-kortit aina 1. rivillä; muut osiot admin-järjestyksessä */
    const showSummaryCards = orderedSections.some((s) => s.id === 'summary');
    const orderedSectionsContent = useMemo(
        () => orderedSections.filter((s) => s.id !== 'summary'),
        [orderedSections]
    );

    if (loading) {
        return (
            <div className="statistics-loading">
                <Loader2 size={32} className="spin" />
                <span>Ladataan tilastoja...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="statistics-error">
                <span>⚠️ Virhe: {error}</span>
                <button onClick={fetchStatistics}>Yritä uudelleen</button>
            </div>
        );
    }

    return (
        <div className="statistics-container">
            <h2 className="statistics-title">
                <BarChart3 size={28} />
                Pelitilastot
            </h2>

            {showSummaryCards && (
                <div className="stats-summary-grid">
                    <div className="summary-card">
                        <Gamepad2 size={24} className="icon" />
                        <div className="value">{stats?.summary?.totalSessions || 0}</div>
                        <div className="label">Pelisessiota</div>
                    </div>
                    <div className="summary-card">
                        <Target size={24} className="icon" />
                        <div className="value">{stats?.summary?.totalGames || 0}</div>
                        <div className="label">Eri peliä</div>
                    </div>
                    <div className="summary-card">
                        <Users size={24} className="icon" />
                        <div className="value">{stats?.summary?.totalPlayers || 0}</div>
                        <div className="label">Pelaajaa</div>
                    </div>
                    <div className="summary-card">
                        <Clock size={24} className="icon" />
                        <div className="value">{formatDuration(stats?.summary?.totalMinutes)}</div>
                        <div className="label">Kokonaisaika</div>
                    </div>
                </div>
            )}

            <StatisticsAnalyticsSections
                stats={stats}
                orderedSections={orderedSectionsContent}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                expandedRows={expandedRows}
                rowSessions={rowSessions}
                loadingRow={loadingRow}
                toggleRowExpansion={toggleRowExpansion}
                formatDate={formatDate}
                formatDuration={formatDuration}
                onSessionClick={handleSessionDetailClick}
            />

            {selectedSession && (
                <SessionDetailOverlay
                    session={selectedSession}
                    onClose={() => setSelectedSession(null)}
                />
            )}

            <style>{`
                .statistics-container {
                    padding: 1.5rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .statistics-title {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    color: #f0f6fc;
                    margin-bottom: 1.5rem;
                    font-size: 1.75rem;
                }

                .statistics-loading, .statistics-error {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 1rem;
                    padding: 3rem;
                    color: #8b949e;
                }

                .spin {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Summary Grid */
                .stats-summary-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 1rem;
                    margin-bottom: 2rem;
                }

                .summary-card {
                    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
                    border: 1px solid #374151;
                    border-radius: 12px;
                    padding: 1.25rem;
                    text-align: center;
                }

                .summary-card .icon {
                    color: #60a5fa;
                    margin-bottom: 0.5rem;
                }

                .summary-card .value {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: #f0f6fc;
                }

                .summary-card .label {
                    font-size: 0.85rem;
                    color: #9ca3af;
                    margin-top: 0.25rem;
                }

                /* Stat Section */
                .stat-section {
                    background: #161b22;
                    border: 1px solid #30363d;
                    border-radius: 12px;
                    margin-bottom: 1rem;
                    overflow: hidden;
                }

                .stat-section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1rem 1.25rem;
                    cursor: pointer;
                    background: rgba(0, 0, 0, 0.2);
                    transition: background 0.2s;
                }

                .stat-section-header:hover {
                    background: rgba(0, 0, 0, 0.3);
                }

                .stat-section-title {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .stat-section-title h3 {
                    margin: 0;
                    font-size: 1.1rem;
                    color: #f0f6fc;
                }

                .stat-section-title small {
                    color: #8b949e;
                    font-size: 0.8rem;
                }

                .stat-section-content {
                    padding: 1rem 1.25rem;
                }

                /* Stats Table */
                .stats-table {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .table-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.5rem 0;
                    border-bottom: 1px solid #30363d;
                    font-size: 0.75rem;
                    color: #8b949e;
                    text-transform: uppercase;
                }

                .table-row {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem 0;
                    border-bottom: 1px solid rgba(48, 54, 61, 0.5);
                }

                .table-row:last-child {
                    border-bottom: none;
                }

                .table-row.clickable {
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .table-row.clickable:hover {
                    background: rgba(96, 165, 250, 0.1);
                }

                .table-row.expanded {
                    background: rgba(96, 165, 250, 0.15);
                    border-bottom-color: #3b82f6;
                }

                .expand-icon {
                    margin-left: auto;
                    color: #6b7280;
                    transition: transform 0.2s;
                    flex-shrink: 0;
                }

                .expand-icon.rotated {
                    transform: rotate(180deg);
                }

                /* Session Details */
                .session-details {
                    background: rgba(0, 0, 0, 0.3);
                    border-left: 3px solid #3b82f6;
                    margin: 0 0 0.5rem 2rem;
                    border-radius: 0 8px 8px 0;
                    padding: 0.75rem;
                }

                .session-details.loading,
                .session-details.empty {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #6b7280;
                    font-size: 0.85rem;
                    padding: 1rem;
                }

                .session-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    max-height: 300px;
                    overflow-y: auto;
                }

                .session-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.5rem;
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 4px;
                    font-size: 0.8rem;
                    flex-wrap: wrap;
                }

                .session-details-hint {
                    font-size: 0.75rem;
                    color: #9ca3af;
                    margin: 0 0 0.5rem 0;
                }

                .session-item.session-item-clickable {
                    cursor: pointer;
                    transition: background 0.15s ease;
                }

                .session-item.session-item-clickable:hover {
                    background: rgba(59, 130, 246, 0.2);
                }

                .session-item.session-item-clickable:focus {
                    outline: 1px solid #3b82f6;
                    outline-offset: 2px;
                }

                .session-date {
                    color: #9ca3af;
                    min-width: 80px;
                }

                .session-game {
                    color: #e5e7eb;
                    font-weight: 500;
                }

                .session-duration {
                    color: #60a5fa;
                    font-size: 0.75rem;
                }

                .session-rank {
                    padding: 0.125rem 0.5rem;
                    border-radius: 4px;
                    font-weight: 600;
                    font-size: 0.75rem;
                }

                .session-rank.rank-1 {
                    background: #fbbf24;
                    color: #000;
                }

                .session-rank.rank-2 {
                    background: #9ca3af;
                    color: #000;
                }

                .session-rank.rank-3 {
                    background: #b45309;
                    color: #fff;
                }

                .session-points {
                    color: #60a5fa;
                    font-weight: 600;
                    font-size: 0.85rem;
                }

                .session-winner {
                    color: #22c55e;
                    font-size: 0.75rem;
                }

                .session-players {
                    color: #6b7280;
                    font-size: 0.75rem;
                    flex: 1;
                    text-align: right;
                }

                .col-rank {
                    width: 32px;
                    text-align: center;
                }

                .rank-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 24px;
                    height: 24px;
                    background: #374151;
                    border-radius: 50%;
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #f0f6fc;
                }

                .table-row:nth-child(2) .rank-badge { background: #fbbf24; color: #000; }
                .table-row:nth-child(3) .rank-badge { background: #9ca3af; color: #000; }
                .table-row:nth-child(4) .rank-badge { background: #b45309; color: #fff; }

                .col-game {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #e5e7eb;
                    font-size: 0.9rem;
                }

                .game-thumb {
                    width: 32px;
                    height: 32px;
                    border-radius: 4px;
                    object-fit: cover;
                }

                .col-stat {
                    width: 80px;
                    text-align: right;
                    color: #9ca3af;
                    font-size: 0.85rem;
                }

                .col-stat.highlight {
                    color: #60a5fa;
                    font-weight: 600;
                }

                /* Streak Cards */
                .streak-cards {
                    display: flex;
                    gap: 1rem;
                    flex-wrap: wrap;
                }

                .streak-card {
                    background: linear-gradient(135deg, #7f1d1d 0%, #450a0a 100%);
                    border-radius: 12px;
                    padding: 1rem;
                    min-width: 160px;
                    text-align: center;
                }

                .streak-flames {
                    display: flex;
                    justify-content: center;
                    gap: 0.25rem;
                    margin-bottom: 0.5rem;
                }

                .flame {
                    color: #f97316;
                    animation: flicker 0.5s ease-in-out infinite alternate;
                }

                @keyframes flicker {
                    from { opacity: 0.8; transform: scale(1); }
                    to { opacity: 1; transform: scale(1.1); }
                }

                .streak-name {
                    font-weight: 600;
                    color: #fbbf24;
                    font-size: 1.1rem;
                }

                .streak-count {
                    color: #fed7aa;
                    font-size: 0.85rem;
                    margin-top: 0.25rem;
                }

                .streak-dates {
                    color: #9ca3af;
                    font-size: 0.75rem;
                    margin-top: 0.5rem;
                }

                /* Closest Games */
                .closest-row {
                    flex-wrap: wrap;
                }

                .closest-row .game-info {
                    flex: 1;
                    min-width: 120px;
                }

                .closest-row .game-info strong {
                    display: block;
                    color: #e5e7eb;
                }

                .closest-row .game-info small {
                    color: #6b7280;
                }

                .closest-row .matchup {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex: 2;
                }

                .closest-row .winner {
                    color: #22c55e;
                    font-weight: 500;
                }

                .closest-row .vs {
                    color: #6b7280;
                    font-size: 0.75rem;
                }

                .closest-row .runnerup {
                    color: #9ca3af;
                }

                .closest-row .diff {
                    color: #f97316;
                    font-weight: 600;
                    width: 60px;
                    text-align: right;
                }

                /* Favorites Grid */
                .favorites-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 1rem;
                }

                .favorite-card {
                    background: rgba(0, 0, 0, 0.2);
                    border: 1px solid #374151;
                    border-radius: 8px;
                    padding: 1rem;
                }

                .fav-player {
                    font-weight: 600;
                    color: #f0f6fc;
                    margin-bottom: 0.5rem;
                }

                .fav-game {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #60a5fa;
                    font-size: 0.9rem;
                }

                .fav-img {
                    width: 28px;
                    height: 28px;
                    border-radius: 4px;
                    object-fit: cover;
                }

                .fav-stats {
                    color: #6b7280;
                    font-size: 0.8rem;
                    margin-top: 0.5rem;
                }

                /* Activity Chart */
                .activity-chart {
                    display: flex;
                    align-items: flex-end;
                    gap: 0.5rem;
                    height: 150px;
                    padding: 1rem 0;
                }

                .bar-container {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    height: 100%;
                }

                .bar {
                    width: 100%;
                    max-width: 40px;
                    background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%);
                    border-radius: 4px 4px 0 0;
                    min-height: 8px;
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                    transition: all 0.3s ease;
                }

                .bar:hover {
                    background: linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%);
                }

                .bar-value {
                    font-size: 0.7rem;
                    color: white;
                    font-weight: 600;
                    padding-top: 4px;
                }

                .bar-label {
                    font-size: 0.7rem;
                    color: #6b7280;
                    margin-top: 0.25rem;
                }

                /* Head to Head */
                .h2h-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .h2h-row {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.5rem 0;
                    border-bottom: 1px solid rgba(48, 54, 61, 0.5);
                }

                .h2h-winner {
                    flex: 1;
                    color: #22c55e;
                    font-weight: 500;
                    text-align: right;
                }

                .h2h-score {
                    width: 50px;
                    text-align: center;
                    background: #374151;
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    color: #f0f6fc;
                    font-weight: 600;
                    font-size: 0.85rem;
                }

                .h2h-loser {
                    flex: 1;
                    color: #9ca3af;
                }

                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .statistics-container {
                        padding: 1rem;
                    }

                    .stats-summary-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .table-header .col-stat:nth-child(n+4),
                    .table-row .col-stat:nth-child(n+4) {
                        display: none;
                    }

                    .activity-chart {
                        overflow-x: auto;
                    }

                    .favorites-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default Statistics;


